import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Nav, Button, Breadcrumb, Spinner } from "react-bootstrap";
import { FaEllipsisH, FaHistory } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import getAPIbaseURL, { getFullName } from "../../../utils/helpers";

import { fetchMyPermissions, fetchUserById } from "../../../redux/slices/userSlice";
import { fetchDelegations } from "../../../redux/slices/delegationSlice";
import {
  filterAbsences,
  fetchUserAbsenceBalance,
} from "../../../redux/slices/absenceSlice";
import { fetchEmployeeFolders } from "../../../redux/slices/organizationDocumentsSlice";
import { ROLES } from "../../../utils/constants/roles";
import {
  fetchTimeBalances,
  fetchBalanceSummary,
} from "../../../redux/slices/timeBalanceSlice";
import { fetchExtendedStatisticsByUser } from "../../../redux/slices/statisticsSlice";

import OverviewTab from "../Tabs/OverviewTab";
import LaborDetailsTab from "../Tabs/LaborDetailsTab";
import PersonalDetailsTab from "../Tabs/PersonalDetailsTab";
import OtherDetailsTab from "../Tabs/OtherDetailsTab";
import EmployeeContractsTab from "../Tabs/EmployeeContractsTab";
import AbsencesTab from "../Tabs/AbsencesTab";
import TimePlanningTab from "../Tabs/TimePlanningTab";
import DocumentsTab from "../Tabs/DocumentsTab";

const VALID_ABSENCE_STATUSES = ["approved", "accepted"];

const EmployeeProfile = ({ employeeId, isOverlay = false, onClose } = {}) => {
  const { id: routeId } = useParams();
  const id = employeeId || routeId;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t, i18n } = useTranslation();

  const [activeTabId, setActiveTabId] = useState("overview");
  const [isFetching, setIsFetching] = useState(true);

  const TABS = useMemo(
    () => [
      { id: "overview", label: t("components.employeeProfile.tabs.overview") },
      { id: "labor_details", label: t("components.employeeProfile.tabs.laborDetails") },
      { id: "personal_details", label: t("components.employeeProfile.tabs.personalDetails") },
      { id: "contracts", label: t("components.employeeProfile.tabs.contracts") },
      { id: "documents", label: t("components.employeeProfile.tabs.documents") },
      { id: "time_planning", label: t("components.employeeProfile.tabs.timePlanning") },
      { id: "absences", label: t("components.employeeProfile.tabs.absences") },
      { id: "tasks", label: t("components.employeeProfile.tabs.tasks") },
      { id: "courses", label: t("components.employeeProfile.tabs.courses") },
      { id: "other", label: t("components.employeeProfile.tabs.other") },
    ],
    [t],
  );

  const currentUser = useSelector((state) => state.users.currentUser);
  const loggedInUser = useSelector((state) => state.auth?.user);
  const permissions = useSelector((state) => state.users?.permissions || {});

  const delegations = useSelector((state) => state.delegations?.items || []);
  const absencesBalances = useSelector((state) => state.absences?.balancesByUserId || {});
  const userAbsences = useSelector((state) => state.absences?.items || []);
  const timeBalances = useSelector((state) => state.timeBalances?.items || []);
  const balanceSummary = useSelector((state) => state.timeBalances?.balanceSummary || {});
  const statistics = useSelector((state) => state.statistics?.data);

  const isOwner = String(loggedInUser?.id) === String(id);
  const canViewSensitiveUsers = permissions.can_view_sensitive_users === true;
  const canManageAccount = permissions.can_delete_users === true;
  const canViewFull = canViewSensitiveUsers || isOwner;
  const canViewEmployeeTabs = canViewSensitiveUsers || isOwner;
  const workerSummaryTab = useMemo(
    () => ({
      id: "personal_details",
      label: t("components.employeeProfile.tabs.personalDetails"),
    }),
    [t],
  );

  // Absence approvers may read any balance; the backend allows Super Admins and Managers by role.
  const canViewOthersBalance = [ROLES.SUPER_ADMIN, ROLES.MANAGER].includes(permissions.role);

  // Owners always have a documents file. For anyone else the backend decides per folder grant,
  // so the tab is only offered once it confirms there is something this viewer may open.
  const [hasDocumentAccess, setHasDocumentAccess] = useState(false);
  useEffect(() => {
    if (!id || isOwner) {
      setHasDocumentAccess(isOwner);
      return undefined;
    }
    let active = true;
    setHasDocumentAccess(false);
    dispatch(fetchEmployeeFolders(id))
      .unwrap()
      .then((folders) => active && setHasDocumentAccess(folders.length > 0))
      .catch(() => active && setHasDocumentAccess(false));
    return () => { active = false; };
  }, [dispatch, id, isOwner]);

  const visibleTabs = useMemo(() => {
    const roleTabs = canViewEmployeeTabs
      ? TABS
      : isOwner
      ? TABS.filter(tab => ["overview", "personal_details", "documents"].includes(tab.id))
      : [workerSummaryTab, TABS.find(tab => tab.id === "documents")];

    return roleTabs.filter(tab =>
      (tab.id !== "other" || canManageAccount) && (tab.id !== "documents" || hasDocumentAccess),
    );
  }, [canManageAccount, canViewEmployeeTabs, hasDocumentAccess, isOwner, TABS, workerSummaryTab]);

  useEffect(() => {
    dispatch(fetchMyPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (!visibleTabs.find((t) => t.id === activeTabId)) {
      setActiveTabId(visibleTabs[0]?.id || "labor_details");
    }
  }, [visibleTabs, activeTabId]);

  useEffect(() => {
    const loadProfileData = async () => {
      setIsFetching(true);
      try {
        await dispatch(fetchUserById(id)).unwrap();

        if (!canViewFull) {
          return;
        }

        if (delegations.length === 0) {
          await dispatch(fetchDelegations());
        }
        // Only this employee's balance is shown; the all-users endpoint is Super Admin only.
        if (isOwner || canViewOthersBalance) {
          await dispatch(fetchUserAbsenceBalance({ userId: id }));
        }
        await dispatch(filterAbsences({ user_id: id }));

        const now = new Date();
        await dispatch(
          fetchTimeBalances({
            userId: id,
            year: now.getFullYear(),
            month: now.getMonth() + 1,
          }),
        );
        await dispatch(fetchBalanceSummary(id));
        await dispatch(fetchExtendedStatisticsByUser(id));
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    };

    if (id) {
      loadProfileData();
    }
  }, [id, dispatch, delegations.length, canViewFull, isOwner, canViewOthersBalance]);

  const delegationName = useMemo(() => {
    if (!currentUser) return "N/A";
    const delegation = currentUser.delegation || delegations.find(
      (d) => String(d.id) === String(currentUser.delegation_id),
    );
    return delegation?.name || "N/A";
  }, [currentUser, delegations]);

  const userBalance = absencesBalances[id];
  const availableVacationDays =
    userBalance && userBalance.available_minutes
      ? Math.floor(userBalance.available_minutes / 480)
      : 0;

  const nextAbsence = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming = userAbsences
      .filter((a) => {
        const belongsToUser = String(a.user_id) === String(id);
        const startDate = new Date(a.start_date);
        const isApproved =
          a.status && VALID_ABSENCE_STATUSES.includes(a.status.toLowerCase());
        return belongsToUser && startDate >= today && isApproved;
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    return upcoming.length > 0 ? upcoming[0] : null;
  }, [userAbsences, id]);

  const currentLang = i18n.resolvedLanguage || i18n.language || "pt-PT";

  const formatAbsenceDate = (dateString) => {
    if (!dateString) return { month: "", day: "" };
    const d = new Date(dateString);
    const month = d
      .toLocaleDateString(currentLang, { month: "short" })
      .toUpperCase()
      .replace(".", "");
    const day = String(d.getDate()).padStart(2, "0");
    return { month, day };
  };

  const currentMonthName = new Date().toLocaleDateString(currentLang, {
    month: "long",
  });
  const capitalizedMonthName =
    currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1);

  const timeStats = useMemo(() => {
    let worked = 0;
    let expected = 0;
    let alerts = 0;
    const today = new Date().toISOString().split("T")[0];

    timeBalances.forEach((tb) => {
      worked += tb.worked_minutes || 0;
      expected += tb.expected_minutes || 0;
      if (
        tb.date < today &&
        tb.expected_minutes > 0 &&
        tb.worked_minutes === 0
      ) {
        alerts += 1;
      }
    });

    const balance = worked - expected;
    const progress =
      expected > 0 ? Math.min(100, Math.round((worked / expected) * 100)) : 0;
    const remaining = Math.max(0, expected - worked);

    return { worked, expected, balance, progress, remaining, alerts };
  }, [timeBalances]);

  const formatMins = (mins) => {
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    const sign = mins < 0 ? "-" : mins > 0 ? "+" : "";
    return `${sign}${h}h ${m.toString().padStart(2, "0")}m`;
  };

  const handleBackNavigation = () => {
    if (isOverlay) {
      onClose?.();
      return;
    }
    navigate(-1);
  };

  if (!id || isFetching || !currentUser || String(currentUser.id) !== String(id)) {
    return (
      <Container
        fluid
        className="bg-main h-100 d-flex justify-content-center align-items-center"
        style={{ overflowY: "auto" }}
      >
        <Spinner animation="border" style={{ color: "var(--brand-primary)" }} />
      </Container>
    );
  }

  const employeeName = getFullName(currentUser);

  const getProfilePic = () => {
    if (currentUser.profile_picture) {
      return currentUser.profile_picture.startsWith("http")
        ? currentUser.profile_picture
        : `${getAPIbaseURL()}${currentUser.profile_picture}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(employeeName)}&background=F3F4F6&color=17296b`;
  };

  const pic = getProfilePic();

  return (
    <Container
      fluid
      className="bg-main h-100 p-4 custom-scrollbar"
      style={{ overflowY: "auto" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Breadcrumb className="mb-0">
          <Breadcrumb.Item onClick={handleBackNavigation}>
            {t("components.employeeProfile.breadcrumb.organization")}
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{employeeName}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <img
            src={pic}
            alt={employeeName}
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <h2 className="m-0 fw-bold txt-main">{employeeName}</h2>
        </div>

        <div className="d-flex gap-2">
          <Button variant="secondary" className="px-3">
            <FaEllipsisH className="txt-subtle" />
          </Button>
          <Button variant="secondary" className="px-3">
            <FaHistory className="txt-subtle" />
          </Button>
          {isOverlay && (
            <Button variant="secondary" className="px-3" onClick={onClose}>
              &times;
            </Button>
          )}
        </div>
      </div>

      <Nav
        variant="tabs"
        className="mb-4"
        style={{ borderBottom: "2px solid var(--gray-200)" }}
      >
        {visibleTabs.map((tab) => (
          <Nav.Item key={tab.id}>
            <Nav.Link
              className={`border-0 pb-3 fw-semibold ${activeTabId === tab.id ? "txt-main" : "txt-subtle"}`}
              style={{
                borderBottom:
                  activeTabId === tab.id
                    ? "2px solid var(--text-primary)"
                    : "2px solid transparent",
                backgroundColor: "transparent",
                borderRadius: 0,
                marginBottom: "-2px",
              }}
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}{" "}
              {tab.id === "courses" && (
                <span style={{ color: "#F59E0B" }}>⚡</span>
              )}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {activeTabId === "overview" && (
        <OverviewTab
          currentUser={currentUser}
          statistics={statistics}
          timeStats={timeStats}
          availableVacationDays={availableVacationDays}
          nextAbsence={nextAbsence}
          capitalizedMonthName={capitalizedMonthName}
          formatMins={formatMins}
          formatAbsenceDate={formatAbsenceDate}
          delegationName={delegationName}
          balanceSummary={balanceSummary}
        />
      )}

     {activeTabId === "labor_details" && (
        <LaborDetailsTab currentUser={currentUser} userId={id} onNavigateToTimePlanning={() => setActiveTabId("time_planning")} />
      )}

      {activeTabId === "time_planning" && (
        <TimePlanningTab currentUser={currentUser} userId={id} />
      )}

      {activeTabId === "personal_details" && (
        <PersonalDetailsTab currentUser={currentUser} userId={id} />
      )}

      {activeTabId === "contracts" && (
        <EmployeeContractsTab currentUser={currentUser} userId={id} />
      )}

      {activeTabId === "documents" && <DocumentsTab userId={id} />}

      {activeTabId === "absences" && <AbsencesTab userId={id} />}

      {activeTabId === "other" && canManageAccount && (
        <OtherDetailsTab currentUser={currentUser} userId={id} />
      )}

      {!["overview", "labor_details", "personal_details", "contracts", "documents", "time_planning", "absences", "other"].includes(activeTabId) && (
        <div className="py-5 text-center txt-subtle">
          <h4>{visibleTabs.find((t) => t.id === activeTabId)?.label}</h4>
          <p>{t("components.employeeProfile.inDevelopment")}</p>
        </div>
      )}
    </Container>
  );
};

export default EmployeeProfile;
